import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, ShieldAlert, Plus, Trash2,
  RefreshCw, CheckCircle, XCircle, Building2,
  TrendingDown, Activity, BarChart2, Bell, Settings,
  ChevronDown, ChevronRight, Zap, Cpu, Image, Flag, GitBranch, Users,
  Download,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL } from '../config';

// ─────────────────────────────────────────────────────────────────────────────
//  Types
// ─────────────────────────────────────────────────────────────────────────────

interface BrandEntity {
  id: number;
  name: string;
  aliases: string[];
  executives: string[];
  products: string[];
  keywords: string[];
  domain?: string;
  industry?: string;
  is_active: boolean;
  monitoring_level: 'standard' | 'enhanced' | 'critical';
  created_at?: string;
}

interface DashboardStats {
  brand_id: number;
  brand_name: string;
  total_mentions: number;
  negative_mentions: number;
  positive_mentions: number;
  neutral_mentions: number;
  high_risk_mentions: number;
  avg_sentiment_score: number;
  avg_risk_score: number;
  total_alerts: number;
  unresolved_alerts: number;
  critical_alerts: number;
  high_alerts: number;
  medium_alerts: number;
  low_alerts: number;
  daily_volume: Record<string, number>;
  platform_breakdown: Record<string, number>;
}

interface BrandAlert {
  id: number;
  brand_id: number;
  brand_name?: string;
  alert_type: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  message: string;
  status: 'UNRESOLVED' | 'RESOLVED' | 'DISMISSED';
  risk_score?: number;
  created_at: string;
}

// ─────────────────────────────────────────────────────────────────────────────
//  API helpers
// ─────────────────────────────────────────────────────────────────────────────

async function apiFetch<T>(path: string, opts?: RequestInit): Promise<T> {
  const ctrl = new AbortController();
  const tid = setTimeout(() => ctrl.abort(), 15_000);
  try {
    const res = await fetch(`${API_BASE_URL}${path}`, {
      signal: ctrl.signal,
      headers: { 'Content-Type': 'application/json', ...(opts?.headers ?? {}) },
      ...opts,
    });
    if (!res.ok) {
      const body = await res.text();
      throw new Error(`${res.status}: ${body}`);
    }
    return res.json();
  } finally {
    clearTimeout(tid);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────────────────

const SEVERITY_STYLES: Record<string, { badge: string; dot: string }> = {
  CRITICAL: { badge: 'bg-rose-500/15 text-rose-400 border-rose-500/30', dot: 'bg-rose-500' },
  HIGH: { badge: 'bg-orange-500/15 text-orange-400 border-orange-500/30', dot: 'bg-orange-500' },
  MEDIUM: { badge: 'bg-amber-500/15 text-amber-400 border-amber-500/30', dot: 'bg-amber-500' },
  LOW: { badge: 'bg-blue-500/15 text-blue-400 border-blue-500/30', dot: 'bg-blue-400' },
};

const LEVEL_BADGE: Record<string, string> = {
  critical: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
  enhanced: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  standard: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
};

function fmt(ts?: string) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}

function riskColor(score?: number) {
  if (!score) return 'text-zinc-400';
  if (score >= 0.8) return 'text-rose-400';
  if (score >= 0.6) return 'text-orange-400';
  if (score >= 0.35) return 'text-amber-400';
  return 'text-emerald-400';
}

function StatCard({ label, value, icon: Icon, color }: { label: string; value: number | string; icon: React.ElementType; color: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-900/60 border border-zinc-800/60 rounded-2xl p-5 relative overflow-hidden group"
    >
      <div className={`absolute -right-5 -top-5 w-20 h-20 ${color.replace('text-', 'bg-').replace('400', '400/10')} rounded-full blur-2xl`} />
      <div className={`inline-flex p-2.5 rounded-xl ${color.replace('text-', 'bg-').replace('400', '400/10')} mb-3`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-xs text-zinc-400 font-medium mt-0.5">{label}</p>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Tab: Overview
// ─────────────────────────────────────────────────────────────────────────────

function OverviewTab({ entities }: { entities: BrandEntity[] }) {
  const [selectedId, setSelectedId] = useState<number | null>(entities[0]?.id ?? null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<string | null>(null);

  const loadStats = useCallback(async (brandId: number) => {
    setLoading(true);
    try {
      const data = await apiFetch<DashboardStats>(`/api/brand/dashboard/${brandId}?days=7`);
      setStats(data);
    } catch {
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedId) loadStats(selectedId);
  }, [selectedId, loadStats]);

  const triggerScan = async () => {
    if (!selectedId) return;
    setScanning(true);
    setScanResult(null);
    try {
      const res = await apiFetch<any>(`/api/brand/scan?brand_id=${selectedId}&hours=24`, { method: 'POST' });
      setScanResult(res.message);
      await loadStats(selectedId);
    } catch (e: any) {
      setScanResult(`Scan error: ${e.message}`);
    } finally {
      setScanning(false);
    }
  };

  if (!entities.length) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <ShieldAlert className="w-12 h-12 text-zinc-400 mb-4" />
        <p className="text-zinc-400 text-sm">No brands configured yet.</p>
        <p className="text-zinc-400 text-xs mt-1">Switch to the Configuration tab to add your first brand.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Brand selector + scan button */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex-1 min-w-[200px] relative">
          <select
            value={selectedId ?? ''}
            onChange={(e) => setSelectedId(Number(e.target.value))}
            className="w-full bg-zinc-900/60 border border-zinc-700/60 text-zinc-200 text-sm rounded-xl px-4 py-2.5 appearance-none focus:outline-none focus:border-cyan-500/50"
          >
            {entities.map((e) => (
              <option key={e.id} value={e.id}>{e.name} ({e.industry ?? 'Industry'})</option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-zinc-400 pointer-events-none" />
        </div>
        <button
          onClick={() => window.open(`${API_BASE_URL}/api/export/brand.csv?limit=5000`, '_blank')}
          className="flex items-center gap-2 px-3 py-2.5 bg-emerald-900/40 hover:bg-emerald-800/50 text-emerald-400 text-sm font-medium rounded-xl border border-emerald-700/40 transition-all"
          title="Export brand alerts as CSV"
        >
          <Download className="w-4 h-4" /> CSV
        </button>
        <button
          onClick={() => selectedId && loadStats(selectedId)}
          className="flex items-center gap-2 px-4 py-2.5 bg-zinc-800/60 hover:bg-zinc-700/60 text-zinc-300 text-sm font-medium rounded-xl border border-zinc-700/40 transition-all"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
        <button
          onClick={triggerScan}
          disabled={scanning}
          className="flex items-center gap-2 px-4 py-2.5 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 text-sm font-semibold rounded-xl border border-cyan-500/20 transition-all disabled:opacity-50"
        >
          {scanning ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          {scanning ? 'Scanning…' : 'Scan Now'}
        </button>
      </div>

      {scanResult && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-sm">
          {scanResult}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-28 bg-zinc-800/40 animate-pulse rounded-2xl" />)}
        </div>
      ) : stats ? (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard label="Total Mentions (7d)" value={stats.total_mentions} icon={Activity} color="text-blue-400" />
            <StatCard label="Negative Mentions" value={stats.negative_mentions} icon={TrendingDown} color="text-rose-400" />
            <StatCard label="High Risk Mentions" value={stats.high_risk_mentions} icon={ShieldAlert} color="text-orange-400" />
            <StatCard label="Unresolved Alerts" value={stats.unresolved_alerts} icon={Bell} color="text-amber-400" />
          </div>

          {/* Sentiment + Risk row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-2xl p-5">
              <p className="text-xs text-zinc-400 font-medium uppercase tracking-wider mb-3">Sentiment Overview</p>
              <div className="flex gap-1 h-3 rounded-full overflow-hidden mb-3">
                <div className="bg-emerald-500/80" style={{ width: `${stats.total_mentions ? (stats.positive_mentions / stats.total_mentions) * 100 : 33}%` }} />
                <div className="bg-zinc-600/80" style={{ width: `${stats.total_mentions ? (stats.neutral_mentions / stats.total_mentions) * 100 : 33}%` }} />
                <div className="bg-rose-500/80" style={{ width: `${stats.total_mentions ? (stats.negative_mentions / stats.total_mentions) * 100 : 34}%` }} />
              </div>
              <div className="flex justify-between text-xs text-zinc-400">
                <span className="text-emerald-400">Positive: {stats.positive_mentions}</span>
                <span className="text-zinc-400">Neutral: {stats.neutral_mentions}</span>
                <span className="text-rose-400">Negative: {stats.negative_mentions}</span>
              </div>
            </div>

            <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-2xl p-5">
              <p className="text-xs text-zinc-400 font-medium uppercase tracking-wider mb-3">Avg Risk Score</p>
              <p className={`text-4xl font-bold ${riskColor(stats.avg_risk_score)}`}>
                {(stats.avg_risk_score * 100).toFixed(0)}
                <span className="text-lg text-zinc-400">/100</span>
              </p>
              <p className="text-xs text-zinc-400 mt-2">Avg Sentiment: {(stats.avg_sentiment_score * 100).toFixed(0)}/100</p>
            </div>

            <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-2xl p-5">
              <p className="text-xs text-zinc-400 font-medium uppercase tracking-wider mb-3">Alert Severity Breakdown</p>
              <div className="space-y-2">
                {[
                  { label: 'Critical', count: stats.critical_alerts, color: 'bg-rose-500' },
                  { label: 'High', count: stats.high_alerts, color: 'bg-orange-500' },
                  { label: 'Medium', count: stats.medium_alerts, color: 'bg-amber-500' },
                  { label: 'Low', count: stats.low_alerts, color: 'bg-blue-400' },
                ].map(({ label, count, color }) => (
                  <div key={label} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${color}`} />
                      <span className="text-zinc-400">{label}</span>
                    </div>
                    <span className="text-zinc-200 font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Platform breakdown */}
          {Object.keys(stats.platform_breakdown).length > 0 && (
            <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-2xl p-5 mb-6">
              <p className="text-xs text-zinc-400 font-medium uppercase tracking-wider mb-4">Platform Breakdown</p>
              <div className="flex flex-wrap gap-3">
                {Object.entries(stats.platform_breakdown).map(([platform, count]) => (
                  <div key={platform} className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800/60 rounded-lg border border-zinc-700/40 text-xs">
                    <span className="text-zinc-300 font-semibold capitalize">{platform}</span>
                    <span className="text-zinc-400">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="py-12 text-center text-zinc-400 text-sm">No data available — try scanning now.</div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Tab: Configuration
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  name: '',
  domain: '',
  industry: '',
  monitoring_level: 'standard' as const,
  aliases: '',
  executives: '',
  products: '',
  keywords: '',
};

function ConfigTab({ entities, onRefresh }: { entities: BrandEntity[]; onRefresh: () => void }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  const handleCreate = async () => {
    if (!form.name.trim()) { setError('Brand name is required'); return; }
    setSaving(true); setError(null); setSuccess(null);
    try {
      await apiFetch('/api/brand/entities', {
        method: 'POST',
        body: JSON.stringify({
          name: form.name.trim(),
          domain: form.domain.trim() || undefined,
          industry: form.industry.trim() || undefined,
          monitoring_level: form.monitoring_level,
          aliases: form.aliases ? form.aliases.split(',').map(s => s.trim()).filter(Boolean) : [],
          executives: form.executives ? form.executives.split(',').map(s => s.trim()).filter(Boolean) : [],
          products: form.products ? form.products.split(',').map(s => s.trim()).filter(Boolean) : [],
          keywords: form.keywords ? form.keywords.split(',').map(s => s.trim()).filter(Boolean) : [],
        }),
      });
      setSuccess(`Brand "${form.name}" added successfully.`);
      setForm(EMPTY_FORM);
      onRefresh();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id: number, name: string) => {
    try {
      await apiFetch(`/api/brand/entities/${id}`, { method: 'DELETE' });
      onRefresh();
    } catch (e: any) {
      setError(`Failed to deactivate ${name}: ${e.message}`);
    }
  };

  const triggerScan = async (brandId: number) => {
    await apiFetch(`/api/brand/scan?brand_id=${brandId}&hours=24`, { method: 'POST' });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Add brand form */}
      <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-2xl p-6">
        <h3 className="text-sm font-bold text-zinc-300 mb-5 flex items-center gap-2">
          <Plus className="w-4 h-4 text-cyan-400" /> Add Brand Entity
        </h3>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-semibold text-zinc-300 mb-1.5 block">Brand Name *</label>
            <input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Acme Corporation"
              className="w-full bg-zinc-800 border border-zinc-600 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/20 placeholder:text-zinc-400"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold text-zinc-300 mb-1.5 block">Domain</label>
              <input
                value={form.domain}
                onChange={e => setForm(f => ({ ...f, domain: e.target.value }))}
                placeholder="acmecorp.com"
                className="w-full bg-zinc-800 border border-zinc-600 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/20 placeholder:text-zinc-400"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-zinc-300 mb-1.5 block">Industry</label>
              <input
                value={form.industry}
                onChange={e => setForm(f => ({ ...f, industry: e.target.value }))}
                placeholder="Finance, Tech, …"
                className="w-full bg-zinc-800 border border-zinc-600 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/20 placeholder:text-zinc-400"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold text-zinc-300 mb-1.5 block">Monitoring Level</label>
            <select
              value={form.monitoring_level}
              onChange={e => setForm(f => ({ ...f, monitoring_level: e.target.value as any }))}
              className="w-full bg-zinc-800/60 border border-zinc-700/50 text-zinc-200 text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-cyan-500/50"
            >
              <option value="standard">Standard</option>
              <option value="enhanced">Enhanced</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          <div>
            <label className="text-sm font-semibold text-zinc-300 mb-1.5 block">Aliases <span className="text-zinc-400 font-normal">(comma-separated)</span></label>
            <input
              value={form.aliases}
              onChange={e => setForm(f => ({ ...f, aliases: e.target.value }))}
              placeholder="Acme, ACME Corp, acme"
              className="w-full bg-zinc-800 border border-zinc-600 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/20 placeholder:text-zinc-400"
            />
          </div>

          <div>
            <label className="text-sm font-semibold text-zinc-300 mb-1.5 block">Executives <span className="text-zinc-400 font-normal">(comma-separated)</span></label>
            <input
              value={form.executives}
              onChange={e => setForm(f => ({ ...f, executives: e.target.value }))}
              placeholder="Jane Doe, John Smith"
              className="w-full bg-zinc-800 border border-zinc-600 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/20 placeholder:text-zinc-400"
            />
          </div>

          <div>
            <label className="text-sm font-semibold text-zinc-300 mb-1.5 block">Products <span className="text-zinc-400 font-normal">(comma-separated)</span></label>
            <input
              value={form.products}
              onChange={e => setForm(f => ({ ...f, products: e.target.value }))}
              placeholder="AcmePay, AcmeCloud"
              className="w-full bg-zinc-800 border border-zinc-600 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/20 placeholder:text-zinc-400"
            />
          </div>

          <div>
            <label className="text-sm font-semibold text-zinc-300 mb-1.5 block">Keywords <span className="text-zinc-400 font-normal">(comma-separated)</span></label>
            <input
              value={form.keywords}
              onChange={e => setForm(f => ({ ...f, keywords: e.target.value }))}
              placeholder="data breach, fraud, outage"
              className="w-full bg-zinc-800 border border-zinc-600 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/20 placeholder:text-zinc-400"
            />
          </div>

          {error && <p className="text-rose-400 text-xs bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">{error}</p>}
          {success && <p className="text-emerald-400 text-xs bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">{success}</p>}

          <button
            onClick={handleCreate}
            disabled={saving}
            className="w-full py-2.5 bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-400 font-semibold text-sm rounded-xl border border-cyan-500/20 transition-all disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Add Brand'}
          </button>
        </div>
      </div>

      {/* Configured brands list */}
      <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-2xl p-6">
        <h3 className="text-sm font-bold text-zinc-300 mb-5 flex items-center gap-2">
          <Building2 className="w-4 h-4 text-blue-400" /> Configured Brands ({entities.length})
        </h3>
        {entities.length === 0 ? (
          <p className="text-zinc-400 text-sm text-center py-8">No brands yet.</p>
        ) : (
          <div className="space-y-2">
            {entities.map(e => (
              <div key={e.id} className="bg-zinc-800/40 border border-zinc-700/40 rounded-xl overflow-hidden">
                <div
                  className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-zinc-800/60 transition-colors"
                  onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                >
                  <div className="flex items-center gap-3">
                    <Shield className="w-4 h-4 text-cyan-400" />
                    <div>
                      <p className="text-sm font-semibold text-zinc-200">{e.name}</p>
                      <p className="text-xs text-zinc-400">{e.industry ?? 'No industry'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded border ${LEVEL_BADGE[e.monitoring_level]}`}>
                      {e.monitoring_level}
                    </span>
                    {expanded === e.id ? <ChevronDown className="w-4 h-4 text-zinc-400" /> : <ChevronRight className="w-4 h-4 text-zinc-400" />}
                  </div>
                </div>
                <AnimatePresence>
                  {expanded === e.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 space-y-2 text-xs text-zinc-400">
                        {e.domain && <p>Domain: <span className="text-zinc-200">{e.domain}</span></p>}
                        {e.aliases.length > 0 && <p>Aliases: <span className="text-zinc-200">{e.aliases.join(', ')}</span></p>}
                        {e.executives.length > 0 && <p>Executives: <span className="text-zinc-200">{e.executives.join(', ')}</span></p>}
                        {e.products.length > 0 && <p>Products: <span className="text-zinc-200">{e.products.join(', ')}</span></p>}
                        {e.keywords.length > 0 && <p>Keywords: <span className="text-zinc-200">{e.keywords.join(', ')}</span></p>}
                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={() => triggerScan(e.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 rounded-lg border border-cyan-500/20 text-xs font-medium transition-all"
                          >
                            <Zap className="w-3.5 h-3.5" /> Scan Now
                          </button>
                          <button
                            onClick={() => handleDeactivate(e.id, e.name)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 rounded-lg border border-rose-500/20 text-xs font-medium transition-all"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Deactivate
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Tab: Alerts
// ─────────────────────────────────────────────────────────────────────────────

function AlertsTab({ entities }: { entities: BrandEntity[] }) {
  const [alerts, setAlerts] = useState<BrandAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterBrand, setFilterBrand] = useState<number | 'all'>('all');
  const [filterSev, setFilterSev] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('UNRESOLVED');

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (filterBrand !== 'all') params.set('brand_id', String(filterBrand));
      if (filterSev !== 'all') params.set('severity', filterSev);
      if (filterStatus !== 'all') params.set('status', filterStatus);
      const data = await apiFetch<BrandAlert[]>(`/api/brand/alerts?${params}`);
      setAlerts(data);
    } catch {
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [filterBrand, filterSev, filterStatus]);

  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  const resolve = async (id: number) => {
    await apiFetch(`/api/brand/alerts/${id}/resolve`, { method: 'POST', body: JSON.stringify({ notes: null }) });
    loadAlerts();
  };

  const dismiss = async (id: number) => {
    await apiFetch(`/api/brand/alerts/${id}/dismiss`, { method: 'POST' });
    loadAlerts();
  };

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <select
          value={filterBrand}
          onChange={e => setFilterBrand(e.target.value === 'all' ? 'all' : Number(e.target.value))}
          className="bg-zinc-900/60 border border-zinc-700/60 text-zinc-300 text-xs rounded-xl px-3 py-2 focus:outline-none"
        >
          <option value="all">All Brands</option>
          {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <select
          value={filterSev}
          onChange={e => setFilterSev(e.target.value)}
          className="bg-zinc-900/60 border border-zinc-700/60 text-zinc-300 text-xs rounded-xl px-3 py-2 focus:outline-none"
        >
          <option value="all">All Severities</option>
          <option value="CRITICAL">Critical</option>
          <option value="HIGH">High</option>
          <option value="MEDIUM">Medium</option>
          <option value="LOW">Low</option>
        </select>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="bg-zinc-900/60 border border-zinc-700/60 text-zinc-300 text-xs rounded-xl px-3 py-2 focus:outline-none"
        >
          <option value="all">All Statuses</option>
          <option value="UNRESOLVED">Unresolved</option>
          <option value="RESOLVED">Resolved</option>
          <option value="DISMISSED">Dismissed</option>
        </select>
        <button
          onClick={loadAlerts}
          className="flex items-center gap-1.5 px-3 py-2 bg-zinc-800/60 hover:bg-zinc-700/60 text-zinc-300 text-xs font-medium rounded-xl border border-zinc-700/40 transition-all"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1,2,3,4].map(i => <div key={i} className="h-16 bg-zinc-800/40 animate-pulse rounded-xl" />)}
        </div>
      ) : alerts.length === 0 ? (
        <div className="py-16 text-center">
          <CheckCircle className="w-10 h-10 text-emerald-500/40 mx-auto mb-3" />
          <p className="text-zinc-400 text-sm">No alerts match the current filters.</p>
        </div>
      ) : (
        <div className="space-y-2">
          <AnimatePresence>
            {alerts.map((alert, idx) => {
              const sev = SEVERITY_STYLES[alert.severity] ?? SEVERITY_STYLES.LOW;
              return (
                <motion.div
                  key={alert.id}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ delay: idx * 0.03 }}
                  className="relative bg-zinc-900/60 border border-zinc-800/50 rounded-xl px-4 py-3 flex flex-col sm:flex-row gap-3 sm:items-center hover:border-zinc-700/60 transition-colors"
                >
                  <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl ${sev.dot}`} />
                  <div className="pl-1 flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded border ${sev.badge}`}>
                        {alert.severity}
                      </span>
                      <span className="text-xs text-zinc-400 bg-zinc-800/60 px-2 py-0.5 rounded border border-zinc-700/30 uppercase">
                        {alert.alert_type.replace(/_/g, ' ')}
                      </span>
                      {alert.brand_name && (
                        <span className="text-xs text-blue-400 font-medium">{alert.brand_name}</span>
                      )}
                    </div>
                    <p className="text-sm text-zinc-200 leading-snug">{alert.message}</p>
                    <p className="text-xs text-zinc-400 mt-0.5">{fmt(alert.created_at)}</p>
                  </div>
                  {alert.status === 'UNRESOLVED' && (
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => resolve(alert.id)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded-lg border border-emerald-500/20 transition-all"
                      >
                        <CheckCircle className="w-3.5 h-3.5" /> Resolve
                      </button>
                      <button
                        onClick={() => dismiss(alert.id)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-zinc-800/60 hover:bg-zinc-700/60 text-zinc-400 text-xs font-medium rounded-lg border border-zinc-700/40 transition-all"
                      >
                        <XCircle className="w-3.5 h-3.5" /> Dismiss
                      </button>
                    </div>
                  )}
                  {alert.status !== 'UNRESOLVED' && (
                    <span className={`flex-shrink-0 text-xs font-bold uppercase px-2 py-1 rounded border ${
                      alert.status === 'RESOLVED'
                        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                        : 'bg-zinc-800/60 text-zinc-400 border-zinc-700/30'
                    }`}>
                      {alert.status}
                    </span>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Phase 2: Domains Tab
// ─────────────────────────────────────────────────────────────────────────────

interface DomainEntry {
  id: number; brand_id: number; domain: string; similarity_score: number;
  risk_score: number; detection_method?: string; is_phishing_suspected: boolean;
  is_ssl: boolean; cert_issuer?: string; status: string; source: string; created_at?: string;
}

function DomainsTab({ entities }: { entities: BrandEntity[] }) {
  const [selectedId, setSelectedId] = useState<number | null>(entities[0]?.id ?? null);
  const [domains, setDomains] = useState<DomainEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async (id: number) => {
    setLoading(true);
    try { setDomains(await apiFetch<DomainEntry[]>(`/api/brand/domains?brand_id=${id}&limit=200`)); }
    catch { setDomains([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (selectedId) load(selectedId); }, [selectedId, load]);

  const scan = async () => {
    if (!selectedId) return;
    setScanning(true); setMsg(null);
    try {
      const r = await apiFetch<any>(`/api/brand/domains/scan/${selectedId}`, { method: 'POST' });
      setMsg(`Scan complete — ${r.new_domains ?? 0} new domains found.`);
      load(selectedId);
    } catch (e: any) { setMsg(`Scan error: ${e.message}`); } finally { setScanning(false); }
  };

  const updateStatus = async (id: number, status: string) => {
    await apiFetch(`/api/brand/domains/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) });
    if (selectedId) load(selectedId);
  };

  const STATUS_BADGE: Record<string, string> = {
    flagged: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
    takedown_requested: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
    monitoring: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
    resolved: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-5 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <select value={selectedId ?? ''} onChange={e => setSelectedId(Number(e.target.value))}
            className="w-full bg-zinc-900/60 border border-zinc-700/60 text-zinc-200 text-sm rounded-xl px-4 py-2.5 appearance-none focus:outline-none focus:border-cyan-500/50">
            {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-zinc-400 pointer-events-none" />
        </div>
        <button onClick={scan} disabled={scanning || !selectedId}
          className="flex items-center gap-2 px-4 py-2.5 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 text-sm font-medium rounded-xl border border-cyan-500/20 transition-all disabled:opacity-50">
          {scanning ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          {scanning ? 'Scanning CT Logs…' : 'Scan Domains'}
        </button>
        <button onClick={() => selectedId && load(selectedId)}
          className="flex items-center gap-2 px-3 py-2.5 bg-zinc-800/60 hover:bg-zinc-700/60 text-zinc-300 text-sm rounded-xl border border-zinc-700/40 transition-all">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      {msg && <p className="mb-4 text-sm text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 rounded-xl px-4 py-2">{msg}</p>}
      {loading ? (
        <div className="space-y-2">{[1,2,3,4].map(i => <div key={i} className="h-14 bg-zinc-800/40 animate-pulse rounded-xl" />)}</div>
      ) : domains.length === 0 ? (
        <div className="py-16 text-center"><Shield className="w-10 h-10 text-zinc-700 mx-auto mb-3" /><p className="text-zinc-400 text-sm">No lookalike domains found. Run a scan to populate.</p></div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-zinc-800/60">
          <table className="w-full text-sm text-zinc-300">
            <thead><tr className="text-xs text-zinc-400 uppercase border-b border-zinc-800/60">
              <th className="px-4 py-3 text-left">Domain</th>
              <th className="px-4 py-3 text-left">Similarity</th>
              <th className="px-4 py-3 text-left">Risk</th>
              <th className="px-4 py-3 text-left">Method</th>
              <th className="px-4 py-3 text-left">SSL</th>
              <th className="px-4 py-3 text-left">Phishing?</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-zinc-800/40">
              {domains.map(d => (
                <tr key={d.id} className="hover:bg-zinc-800/20 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-zinc-200">{d.domain}</td>
                  <td className="px-4 py-3"><span className={riskColor(d.similarity_score)}>{(d.similarity_score * 100).toFixed(0)}%</span></td>
                  <td className="px-4 py-3"><span className={riskColor(d.risk_score)}>{(d.risk_score * 100).toFixed(0)}</span></td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{d.detection_method ?? '—'}</td>
                  <td className="px-4 py-3">{d.is_ssl ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-zinc-400" />}</td>
                  <td className="px-4 py-3">{d.is_phishing_suspected ? <ShieldAlert className="w-4 h-4 text-rose-400" /> : <span className="text-zinc-400">—</span>}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded border ${STATUS_BADGE[d.status] ?? STATUS_BADGE.monitoring}`}>{d.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <select onChange={e => updateStatus(d.id, e.target.value)} defaultValue={d.status}
                      className="bg-zinc-800/60 border border-zinc-700/40 text-zinc-400 text-xs rounded-lg px-2 py-1">
                      <option value="monitoring">Monitoring</option>
                      <option value="flagged">Flag</option>
                      <option value="takedown_requested">Takedown</option>
                      <option value="resolved">Resolve</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Phase 2: Impersonation Tab
// ─────────────────────────────────────────────────────────────────────────────

interface ImpersonationAlert2 {
  id: number; brand_id: number; brand_name?: string; platform: string;
  username: string; display_name?: string; profile_url?: string;
  followers_count?: number; similarity_score: number; confidence_score: number;
  impersonation_type?: string; target_entity?: string; status: string;
  created_at?: string;
}

const PLATFORM_BADGE: Record<string, string> = {
  twitter: 'bg-sky-500/15 text-sky-400 border-sky-500/30',
  x: 'bg-sky-500/15 text-sky-400 border-sky-500/30',
  instagram: 'bg-pink-500/15 text-pink-400 border-pink-500/30',
  tiktok: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  facebook: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  linkedin: 'bg-blue-700/15 text-blue-400 border-blue-700/30',
  youtube: 'bg-red-500/15 text-red-400 border-red-500/30',
};

function ImpersonationTab({ entities }: { entities: BrandEntity[] }) {
  const [selectedId, setSelectedId] = useState<number | null>(entities[0]?.id ?? null);
  const [alerts, setAlerts] = useState<ImpersonationAlert2[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ platform: 'twitter', username: '', display_name: '', bio_text: '', followers_count: 0, profile_url: '' });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async (id: number) => {
    setLoading(true);
    try { setAlerts(await apiFetch<ImpersonationAlert2[]>(`/api/brand/impersonation?brand_id=${id}&limit=100`)); }
    catch { setAlerts([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (selectedId) load(selectedId); }, [selectedId, load]);

  const updateStatus = async (id: number, status: string) => {
    await apiFetch(`/api/brand/impersonation/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) });
    if (selectedId) load(selectedId);
  };

  const submit = async () => {
    if (!selectedId) return;
    setSubmitting(true); setMsg(null);
    try {
      const r = await apiFetch<any>(`/api/brand/impersonation/${selectedId}`, { method: 'POST', body: JSON.stringify(form) });
      setMsg(r.status === 'below_threshold' ? 'Account scored — below detection threshold.' : `Flagged: ${r.username} (confidence ${((r.confidence_score ?? 0) * 100).toFixed(0)}%)`);
      setShowForm(false);
      load(selectedId);
    } catch (e: any) { setMsg(`Error: ${e.message}`); } finally { setSubmitting(false); }
  };

  const IMP_STATUS: Record<string, string> = {
    ACTIVE: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
    REPORTED: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    REMOVED: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    FALSE_POSITIVE: 'bg-zinc-700/30 text-zinc-400 border-zinc-700/30',
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-5 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <select value={selectedId ?? ''} onChange={e => setSelectedId(Number(e.target.value))}
            className="w-full bg-zinc-900/60 border border-zinc-700/60 text-zinc-200 text-sm rounded-xl px-4 py-2.5 appearance-none focus:outline-none focus:border-cyan-500/50">
            {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-zinc-400 pointer-events-none" />
        </div>
        <button onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2.5 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 text-sm font-medium rounded-xl border border-cyan-500/20 transition-all">
          <Plus className="w-4 h-4" /> Submit Account
        </button>
        <button onClick={() => selectedId && load(selectedId)}
          className="flex items-center gap-2 px-3 py-2.5 bg-zinc-800/60 hover:bg-zinc-700/60 text-zinc-300 text-sm rounded-xl border border-zinc-700/40 transition-all">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      {msg && <p className="mb-4 text-sm text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 rounded-xl px-4 py-2">{msg}</p>}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="mb-5 bg-zinc-900/60 border border-zinc-800/60 rounded-xl p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {(['platform','username','display_name','bio_text','profile_url'] as const).map(f => (
              <div key={f}>
                <label className="block text-xs text-zinc-400 mb-1 capitalize">{f.replace('_', ' ')}</label>
                <input value={(form as any)[f]} onChange={e => setForm(p => ({ ...p, [f]: e.target.value }))}
                  className="w-full bg-zinc-800/60 border border-zinc-700/40 text-zinc-200 text-sm rounded-xl px-3 py-2 focus:outline-none focus:border-cyan-500/50" />
              </div>
            ))}
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Followers</label>
              <input type="number" value={form.followers_count} onChange={e => setForm(p => ({ ...p, followers_count: +e.target.value }))}
                className="w-full bg-zinc-800/60 border border-zinc-700/40 text-zinc-200 text-sm rounded-xl px-3 py-2 focus:outline-none focus:border-cyan-500/50" />
            </div>
            <div className="sm:col-span-2 flex justify-end gap-3">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-zinc-400 rounded-xl border border-zinc-700/40">Cancel</button>
              <button onClick={submit} disabled={submitting}
                className="px-4 py-2 text-sm font-medium bg-cyan-500/20 text-cyan-400 rounded-xl border border-cyan-500/30 hover:bg-cyan-500/30 transition-all disabled:opacity-50">
                {submitting ? 'Scoring…' : 'Score Account'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 bg-zinc-800/40 animate-pulse rounded-xl" />)}</div>
      ) : alerts.length === 0 ? (
        <div className="py-16 text-center"><Shield className="w-10 h-10 text-zinc-700 mx-auto mb-3" /><p className="text-zinc-400 text-sm">No impersonation alerts. Submit accounts above to begin scoring.</p></div>
      ) : (
        <div className="space-y-2">
          {alerts.map(a => (
            <div key={a.id} className="bg-zinc-900/60 border border-zinc-800/50 rounded-xl px-4 py-3 flex flex-col sm:flex-row gap-3 sm:items-center hover:border-zinc-700/60 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded border ${PLATFORM_BADGE[a.platform.toLowerCase()] ?? 'bg-zinc-700/20 text-zinc-400 border-zinc-700/30'}`}>{a.platform}</span>
                  <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded border ${IMP_STATUS[a.status] ?? IMP_STATUS.ACTIVE}`}>{a.status}</span>
                  {a.brand_name && <span className="text-xs text-blue-400 font-medium">{a.brand_name}</span>}
                </div>
                <p className="text-sm text-zinc-200 font-medium">@{a.username}{a.display_name ? ` — ${a.display_name}` : ''}</p>
                <p className="text-xs text-zinc-400 mt-0.5">Confidence: <span className={riskColor(a.confidence_score)}>{(a.confidence_score * 100).toFixed(0)}%</span> · Similarity: {(a.similarity_score * 100).toFixed(0)}% · {fmt(a.created_at)}</p>
              </div>
              {a.status === 'ACTIVE' && (
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => updateStatus(a.id, 'REPORTED')} className="px-3 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 text-xs font-medium rounded-lg border border-amber-500/20 transition-all">Report</button>
                  <button onClick={() => updateStatus(a.id, 'FALSE_POSITIVE')} className="px-3 py-1.5 bg-zinc-800/60 hover:bg-zinc-700/60 text-zinc-400 text-xs rounded-lg border border-zinc-700/40 transition-all">False +</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Phase 3: Dark Web Tab
// ─────────────────────────────────────────────────────────────────────────────

interface DarkWebMention2 {
  id: number; brand_id: number; brand_name?: string; source_type: string;
  source_name?: string; title?: string; content_snippet?: string;
  risk_score: number; severity: string; contains_credentials: boolean;
  contains_pii: boolean; status: string; created_at?: string;
}

function DarkWebTab({ entities }: { entities: BrandEntity[] }) {
  const [selectedId, setSelectedId] = useState<number | null>(entities[0]?.id ?? null);
  const [mentions, setMentions] = useState<DarkWebMention2[]>([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async (id: number) => {
    setLoading(true);
    try { setMentions(await apiFetch<DarkWebMention2[]>(`/api/brand/dark-web?brand_id=${id}&limit=100`)); }
    catch { setMentions([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (selectedId) load(selectedId); }, [selectedId, load]);

  const scan = async () => {
    if (!selectedId) return;
    setScanning(true); setMsg(null);
    try {
      const r = await apiFetch<any>(`/api/brand/dark-web/scan/${selectedId}`, { method: 'POST' });
      setMsg(`Scan complete — ${r.new_findings ?? 0} new paste site mentions.`);
      load(selectedId);
    } catch (e: any) { setMsg(`Scan error: ${e.message}`); } finally { setScanning(false); }
  };

  const updateStatus = async (id: number, status: string) => {
    await apiFetch(`/api/brand/dark-web/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) });
    if (selectedId) load(selectedId);
  };

  const SOURCE_BADGE: Record<string, string> = {
    paste_site: 'bg-violet-500/15 text-violet-400 border-violet-500/30',
    forum: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    telegram: 'bg-sky-500/15 text-sky-400 border-sky-500/30',
    marketplace: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
    breach_repo: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-5 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <select value={selectedId ?? ''} onChange={e => setSelectedId(Number(e.target.value))}
            className="w-full bg-zinc-900/60 border border-zinc-700/60 text-zinc-200 text-sm rounded-xl px-4 py-2.5 appearance-none focus:outline-none focus:border-cyan-500/50">
            {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-zinc-400 pointer-events-none" />
        </div>
        <button onClick={scan} disabled={scanning || !selectedId}
          className="flex items-center gap-2 px-4 py-2.5 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 text-sm font-medium rounded-xl border border-cyan-500/20 transition-all disabled:opacity-50">
          {scanning ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          {scanning ? 'Scanning Paste Sites…' : 'Scan Paste Sites'}
        </button>
        <button onClick={() => selectedId && load(selectedId)}
          className="flex items-center gap-2 px-3 py-2.5 bg-zinc-800/60 hover:bg-zinc-700/60 text-zinc-300 text-sm rounded-xl border border-zinc-700/40 transition-all">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      {msg && <p className="mb-4 text-sm text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 rounded-xl px-4 py-2">{msg}</p>}
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 bg-zinc-800/40 animate-pulse rounded-xl" />)}</div>
      ) : mentions.length === 0 ? (
        <div className="py-16 text-center"><Shield className="w-10 h-10 text-zinc-700 mx-auto mb-3" /><p className="text-zinc-400 text-sm">No dark web mentions found. Run a scan to check paste sites.</p></div>
      ) : (
        <div className="space-y-3">
          {mentions.map(m => {
            const sev = SEVERITY_STYLES[m.severity] ?? SEVERITY_STYLES.LOW;
            return (
              <div key={m.id} className="relative bg-zinc-900/60 border border-zinc-800/50 rounded-xl px-4 py-3 hover:border-zinc-700/60 transition-colors">
                <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl ${sev.dot}`} />
                <div className="pl-1 flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded border ${sev.badge}`}>{m.severity}</span>
                      <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded border ${SOURCE_BADGE[m.source_type] ?? 'bg-zinc-700/20 text-zinc-400 border-zinc-700/30'}`}>{m.source_type.replace('_', ' ')}</span>
                      {m.contains_credentials && <span className="text-xs bg-rose-500/10 text-rose-400 border border-rose-500/20 px-2 py-0.5 rounded">🔑 Credentials</span>}
                      {m.contains_pii && <span className="text-xs bg-orange-500/10 text-orange-400 border border-orange-500/20 px-2 py-0.5 rounded">👤 PII</span>}
                    </div>
                    {m.title && <p className="text-sm font-medium text-zinc-200 mb-1">{m.title}</p>}
                    {m.content_snippet && <p className="text-xs text-zinc-400 font-mono truncate">{m.content_snippet.substring(0, 160)}…</p>}
                    <p className="text-xs text-zinc-400 mt-1">Risk: <span className={riskColor(m.risk_score)}>{(m.risk_score * 100).toFixed(0)}</span> · {m.source_name ?? 'Unknown source'} · {fmt(m.created_at)}</p>
                  </div>
                  {m.status === 'NEW' && (
                    <div className="flex gap-2 flex-shrink-0 self-start">
                      <button onClick={() => updateStatus(m.id, 'REVIEWED')} className="px-3 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded-lg border border-emerald-500/20 transition-all">Review</button>
                      <button onClick={() => updateStatus(m.id, 'ESCALATED')} className="px-3 py-1.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 text-xs font-medium rounded-lg border border-rose-500/20 transition-all">Escalate</button>
                    </div>
                  )}
                  {m.status !== 'NEW' && (
                    <span className={`flex-shrink-0 text-xs font-bold uppercase px-2 py-1 rounded border self-start ${m.status === 'ESCALATED' ? 'bg-rose-500/10 text-rose-400 border-rose-500/20' : m.status === 'REVIEWED' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-zinc-800/60 text-zinc-400 border-zinc-700/30'}`}>{m.status}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Phase 4: Reports Tab
// ─────────────────────────────────────────────────────────────────────────────

interface BrandReport2 {
  id: number; brand_id: number; brand_name?: string; report_type: string;
  title: string; executive_summary?: string; status: string;
  period_start?: string; period_end?: string; created_at?: string;
  report_data?: Record<string, any>;
}

function ReportsTab({ entities }: { entities: BrandEntity[] }) {
  const [selectedId, setSelectedId] = useState<number | null>(entities[0]?.id ?? null);
  const [reports, setReports] = useState<BrandReport2[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [days, setDays] = useState(7);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async (id: number) => {
    setLoading(true);
    try { setReports(await apiFetch<BrandReport2[]>(`/api/brand/reports?brand_id=${id}&limit=20`)); }
    catch { setReports([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (selectedId) load(selectedId); }, [selectedId, load]);

  const generate = async () => {
    if (!selectedId) return;
    setGenerating(true); setMsg(null);
    try {
      await apiFetch<any>(`/api/brand/reports/${selectedId}?days=${days}&report_type=on_demand`, { method: 'POST' });
      setMsg('Report generated successfully.');
      load(selectedId);
    } catch (e: any) { setMsg(`Error: ${e.message}`); } finally { setGenerating(false); }
  };

  const downloadJson = (r: BrandReport2) => {
    const blob = new Blob([JSON.stringify(r.report_data ?? {}, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `brand-report-${r.id}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-5 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <select value={selectedId ?? ''} onChange={e => setSelectedId(Number(e.target.value))}
            className="w-full bg-zinc-900/60 border border-zinc-700/60 text-zinc-200 text-sm rounded-xl px-4 py-2.5 appearance-none focus:outline-none focus:border-cyan-500/50">
            {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-zinc-400 pointer-events-none" />
        </div>
        <select value={days} onChange={e => setDays(Number(e.target.value))}
          className="bg-zinc-900/60 border border-zinc-700/60 text-zinc-300 text-sm rounded-xl px-3 py-2.5 focus:outline-none">
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <button onClick={generate} disabled={generating || !selectedId}
          className="flex items-center gap-2 px-4 py-2.5 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 text-sm font-medium rounded-xl border border-cyan-500/20 transition-all disabled:opacity-50">
          {generating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
          {generating ? 'Generating…' : 'Generate Report'}
        </button>
        <button onClick={() => selectedId && load(selectedId)}
          className="flex items-center gap-2 px-3 py-2.5 bg-zinc-800/60 hover:bg-zinc-700/60 text-zinc-300 text-sm rounded-xl border border-zinc-700/40 transition-all">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      {msg && <p className="mb-4 text-sm text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 rounded-xl px-4 py-2">{msg}</p>}
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 bg-zinc-800/40 animate-pulse rounded-xl" />)}</div>
      ) : reports.length === 0 ? (
        <div className="py-16 text-center"><Activity className="w-10 h-10 text-zinc-700 mx-auto mb-3" /><p className="text-zinc-400 text-sm">No reports yet. Click "Generate Report" to create the first one.</p></div>
      ) : (
        <div className="space-y-3">
          {reports.map(r => (
            <div key={r.id} className="bg-zinc-900/60 border border-zinc-800/50 rounded-xl overflow-hidden hover:border-zinc-700/60 transition-colors">
              <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-200 truncate">{r.title}</p>
                  <p className="text-xs text-zinc-400 mt-0.5">{r.report_type} · {fmt(r.created_at)}</p>
                </div>
                <div className="flex items-center gap-2">
                  {r.report_data && (
                    <>
                      <button onClick={e => { e.stopPropagation(); downloadJson(r); }}
                        className="px-3 py-1.5 bg-zinc-800/60 hover:bg-zinc-700/60 text-zinc-400 text-xs rounded-lg border border-zinc-700/40 transition-all">
                        JSON
                      </button>
                      <button onClick={e => { e.stopPropagation(); window.print(); }}
                        className="px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 text-xs rounded-lg border border-blue-500/20 transition-all">
                        PDF
                      </button>
                    </>
                  )}
                  {expanded === r.id ? <ChevronDown className="w-4 h-4 text-zinc-400" /> : <ChevronRight className="w-4 h-4 text-zinc-400" />}
                </div>
              </div>
              <AnimatePresence>
                {expanded === r.id && r.executive_summary && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    className="border-t border-zinc-800/60 px-4 py-4 bg-zinc-950/40">
                    <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-mono leading-relaxed">{r.executive_summary}</pre>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Phase 3: Brand Attack Simulator Tab
// ─────────────────────────────────────────────────────────────────────────────

interface SimResult {
  id: number; brand_id: number; scenario_name: string; scenario_type: string;
  composite_risk_score: number; peak_backlash_ratio: number;
  predicted_reach_48h: number; boycott_risk: number;
  full_result?: { recommendations?: string[]; timeline_data?: Array<{hour:number;reach:number;negative_ratio:number}> };
  created_at: string;
}

const SIM_SCENARIO_TYPES = [
  { value: 'brand_attack', label: 'Brand Attack' },
  { value: 'data_breach', label: 'Data Breach' },
  { value: 'executive_scandal', label: 'Executive Scandal' },
  { value: 'product_recall', label: 'Product Recall' },
  { value: 'pr_crisis', label: 'PR Crisis' },
];

function SimulatorTab({ entities }: { entities: BrandEntity[] }) {
  const [selectedId, setSelectedId] = useState<number | null>(entities[0]?.id ?? null);
  const [sims, setSims] = useState<SimResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<SimResult | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [form, setForm] = useState({
    scenario_name: '',
    scenario_type: 'brand_attack',
    event_description: '',
    initial_platform: 'twitter',
    estimated_initial_reach: 50000,
    severity_input: 'HIGH',
  });

  const loadSims = useCallback(async (id: number) => {
    setLoading(true);
    try { setSims(await apiFetch<SimResult[]>(`/api/brand/simulations?brand_id=${id}&limit=10`)); }
    catch { setSims([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (selectedId) loadSims(selectedId); }, [selectedId, loadSims]);

  const run = async () => {
    if (!selectedId || !form.scenario_name.trim() || !form.event_description.trim()) {
      setMsg('Please fill in scenario name and event description.'); return;
    }
    setRunning(true); setMsg(null); setResult(null);
    try {
      const r = await apiFetch<SimResult>(`/api/brand/simulate/${selectedId}`, {
        method: 'POST', body: JSON.stringify(form),
      });
      setResult(r);
      loadSims(selectedId);
    } catch (e: any) { setMsg(`Error: ${e.message}`); } finally { setRunning(false); }
  };

  const riskPct = (s: number) => `${(s * 100).toFixed(0)}%`;

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-wrap gap-3 mb-5 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <select value={selectedId ?? ''} onChange={e => setSelectedId(Number(e.target.value))}
            className="w-full bg-zinc-900/60 border border-zinc-700/60 text-zinc-200 text-sm rounded-xl px-4 py-2.5 appearance-none focus:outline-none focus:border-cyan-500/50">
            {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-zinc-400 pointer-events-none" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Simulation form */}
        <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-2xl p-6">
          <h3 className="text-sm font-bold text-zinc-300 mb-4 flex items-center gap-2">
            <Cpu className="w-4 h-4 text-cyan-400" /> Run Simulation
          </h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-zinc-300 mb-1.5 block">Scenario Name *</label>
              <input value={form.scenario_name} onChange={e => setForm(f => ({ ...f, scenario_name: e.target.value }))}
                placeholder="Q4 Competitor Attack Sim"
                className="w-full bg-zinc-800 border border-zinc-600 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/20 placeholder:text-zinc-400" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-zinc-300 mb-1.5 block">Scenario Type</label>
                <select value={form.scenario_type} onChange={e => setForm(f => ({ ...f, scenario_type: e.target.value }))}
                  className="w-full bg-zinc-800/60 border border-zinc-700/50 text-zinc-200 text-sm rounded-xl px-3 py-2.5 focus:outline-none">
                  {SIM_SCENARIO_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-zinc-300 mb-1.5 block">Severity</label>
                <select value={form.severity_input} onChange={e => setForm(f => ({ ...f, severity_input: e.target.value }))}
                  className="w-full bg-zinc-800/60 border border-zinc-700/50 text-zinc-200 text-sm rounded-xl px-3 py-2.5 focus:outline-none">
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="CRITICAL">Critical</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-zinc-300 mb-1.5 block">Event Description *</label>
              <textarea value={form.event_description} onChange={e => setForm(f => ({ ...f, event_description: e.target.value }))}
                rows={3} placeholder="Describe the simulated event…"
                className="w-full bg-zinc-800 border border-zinc-600 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/20 placeholder:text-zinc-400 resize-none" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-zinc-300 mb-1.5 block">Initial Platform</label>
                <select value={form.initial_platform} onChange={e => setForm(f => ({ ...f, initial_platform: e.target.value }))}
                  className="w-full bg-zinc-800/60 border border-zinc-700/50 text-zinc-200 text-sm rounded-xl px-3 py-2.5 focus:outline-none">
                  {['twitter','reddit','news','instagram','tiktok','facebook'].map(p => <option key={p} value={p} className="capitalize">{p}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-zinc-300 mb-1.5 block">Initial Reach</label>
                <input type="number" value={form.estimated_initial_reach}
                  onChange={e => setForm(f => ({ ...f, estimated_initial_reach: Number(e.target.value) }))}
                  className="w-full bg-zinc-800/60 border border-zinc-700/50 text-zinc-200 text-sm rounded-xl px-3 py-2.5 focus:outline-none" />
              </div>
            </div>
            {msg && <p className="text-amber-400 text-xs bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">{msg}</p>}
            <button onClick={run} disabled={running}
              className="w-full py-2.5 bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-400 font-semibold text-sm rounded-xl border border-cyan-500/20 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
              {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Cpu className="w-4 h-4" />}
              {running ? 'Simulating…' : 'Run Simulation'}
            </button>
          </div>
        </div>

        {/* Result + past sims */}
        <div className="space-y-4">
          {result && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="bg-zinc-900/60 border border-cyan-500/20 rounded-2xl p-5">
              <h4 className="text-sm font-bold text-cyan-400 mb-3">Simulation Result: {result.scenario_name}</h4>
              <div className="grid grid-cols-2 gap-3 mb-4">
                {[
                  { label: 'Composite Risk', value: riskPct(result.composite_risk_score), color: riskColor(result.composite_risk_score) },
                  { label: 'Peak Backlash', value: riskPct(result.peak_backlash_ratio), color: riskColor(result.peak_backlash_ratio) },
                  { label: 'Predicted Reach 48h', value: result.predicted_reach_48h?.toLocaleString(), color: 'text-blue-400' },
                  { label: 'Boycott Risk', value: riskPct(result.boycott_risk), color: riskColor(result.boycott_risk) },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-zinc-800/50 rounded-xl p-3 border border-zinc-700/40">
                    <p className={`text-lg font-bold ${color}`}>{value}</p>
                    <p className="text-xs text-zinc-400">{label}</p>
                  </div>
                ))}
              </div>
              {result.full_result?.recommendations && (
                <div>
                  <p className="text-xs text-zinc-400 font-medium mb-2">Recommendations</p>
                  <ul className="space-y-1.5">
                    {result.full_result.recommendations.slice(0, 5).map((rec, i) => (
                      <li key={i} className="flex gap-2 text-xs text-zinc-300">
                        <span className="text-cyan-500 flex-shrink-0">{i + 1}.</span>{rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>
          )}

          <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-2xl p-5">
            <h4 className="text-sm font-bold text-zinc-300 mb-3">Past Simulations</h4>
            {loading ? (
              <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-12 bg-zinc-800/40 animate-pulse rounded-lg" />)}</div>
            ) : sims.length === 0 ? (
              <p className="text-xs text-zinc-400 text-center py-6">No simulations yet.</p>
            ) : (
              <div className="space-y-2">
                {sims.map(s => (
                  <div key={s.id} className="flex items-center justify-between px-3 py-2 bg-zinc-800/40 rounded-lg border border-zinc-700/30">
                    <div>
                      <p className="text-xs font-medium text-zinc-200">{s.scenario_name}</p>
                      <p className="text-xs text-zinc-400">{s.scenario_type} · {fmt(s.created_at)}</p>
                    </div>
                    <span className={`text-sm font-bold ${riskColor(s.composite_risk_score)}`}>{riskPct(s.composite_risk_score)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Phase 3: Logo Misuse Detection Tab
// ─────────────────────────────────────────────────────────────────────────────

interface LogoAlert {
  id: number; brand_id: number; source_url: string; image_url?: string;
  platform: string; context_text?: string; confidence_score: number;
  detection_method?: string; is_authorized: boolean; severity: string;
  status: string; created_at?: string;
}

function LogoMisuseTab({ entities }: { entities: BrandEntity[] }) {
  const [selectedId, setSelectedId] = useState<number | null>(entities[0]?.id ?? null);
  const [alerts, setAlerts] = useState<LogoAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [form, setForm] = useState({ source_url: '', image_url: '', platform: 'website', context_text: '', severity: 'MEDIUM', confidence_score: 1.0 });

  const load = useCallback(async (id: number) => {
    setLoading(true);
    try { setAlerts(await apiFetch<LogoAlert[]>(`/api/brand/logo-misuse?brand_id=${id}&limit=100`)); }
    catch { setAlerts([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (selectedId) load(selectedId); }, [selectedId, load]);

  const submit = async () => {
    if (!selectedId || !form.source_url.trim()) { setMsg('Source URL is required.'); return; }
    setSubmitting(true); setMsg(null);
    try {
      await apiFetch(`/api/brand/logo-misuse/${selectedId}`, { method: 'POST', body: JSON.stringify(form) });
      setMsg('Logo misuse finding submitted.'); setShowForm(false);
      load(selectedId);
    } catch (e: any) { setMsg(`Error: ${e.message}`); } finally { setSubmitting(false); }
  };

  const updateStatus = async (id: number, status: string) => {
    await apiFetch(`/api/brand/logo-misuse/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) });
    if (selectedId) load(selectedId);
  };

  const LOGO_STATUS: Record<string, string> = {
    NEW: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    REVIEWED: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
    TAKEDOWN_REQ: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
    RESOLVED: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-5 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <select value={selectedId ?? ''} onChange={e => setSelectedId(Number(e.target.value))}
            className="w-full bg-zinc-900/60 border border-zinc-700/60 text-zinc-200 text-sm rounded-xl px-4 py-2.5 appearance-none focus:outline-none focus:border-cyan-500/50">
            {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-zinc-400 pointer-events-none" />
        </div>
        <button onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2.5 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 text-sm font-medium rounded-xl border border-cyan-500/20 transition-all">
          <Plus className="w-4 h-4" /> Submit Finding
        </button>
        <button onClick={() => selectedId && load(selectedId)}
          className="flex items-center gap-2 px-3 py-2.5 bg-zinc-800/60 hover:bg-zinc-700/60 text-zinc-300 text-sm rounded-xl border border-zinc-700/40">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      {msg && <p className="mb-4 text-sm text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 rounded-xl px-4 py-2">{msg}</p>}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="mb-5 bg-zinc-900/60 border border-zinc-800/60 rounded-xl p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs text-zinc-400 mb-1">Source URL *</label>
              <input value={form.source_url} onChange={e => setForm(f => ({ ...f, source_url: e.target.value }))} placeholder="https://example.com/page-with-logo"
                className="w-full bg-zinc-800/60 border border-zinc-700/40 text-zinc-200 text-sm rounded-xl px-3 py-2 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Image URL</label>
              <input value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} placeholder="https://example.com/logo.png"
                className="w-full bg-zinc-800/60 border border-zinc-700/40 text-zinc-200 text-sm rounded-xl px-3 py-2 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Platform</label>
              <input value={form.platform} onChange={e => setForm(f => ({ ...f, platform: e.target.value }))} placeholder="website"
                className="w-full bg-zinc-800/60 border border-zinc-700/40 text-zinc-200 text-sm rounded-xl px-3 py-2 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Severity</label>
              <select value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}
                className="w-full bg-zinc-800/60 border border-zinc-700/40 text-zinc-200 text-sm rounded-xl px-3 py-2 focus:outline-none">
                <option value="LOW">Low</option><option value="MEDIUM">Medium</option><option value="HIGH">High</option><option value="CRITICAL">Critical</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Context Text</label>
              <input value={form.context_text} onChange={e => setForm(f => ({ ...f, context_text: e.target.value }))} placeholder="Phishing page using our logo"
                className="w-full bg-zinc-800/60 border border-zinc-700/40 text-zinc-200 text-sm rounded-xl px-3 py-2 focus:outline-none" />
            </div>
            <div className="sm:col-span-2 flex justify-end gap-3">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-zinc-400 rounded-xl border border-zinc-700/40">Cancel</button>
              <button onClick={submit} disabled={submitting}
                className="px-4 py-2 text-sm font-medium bg-cyan-500/20 text-cyan-400 rounded-xl border border-cyan-500/30 hover:bg-cyan-500/30 transition-all disabled:opacity-50">
                {submitting ? 'Submitting…' : 'Submit Finding'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 bg-zinc-800/40 animate-pulse rounded-xl" />)}</div>
      ) : alerts.length === 0 ? (
        <div className="py-16 text-center"><Image className="w-10 h-10 text-zinc-700 mx-auto mb-3" /><p className="text-zinc-400 text-sm">No logo misuse alerts. Submit a finding above.</p></div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-zinc-800/60">
          <table className="w-full text-sm text-zinc-300">
            <thead><tr className="text-xs text-zinc-400 uppercase border-b border-zinc-800/60">
              <th className="px-4 py-3 text-left">Source</th>
              <th className="px-4 py-3 text-left">Platform</th>
              <th className="px-4 py-3 text-left">Severity</th>
              <th className="px-4 py-3 text-left">Confidence</th>
              <th className="px-4 py-3 text-left">Method</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-zinc-800/40">
              {alerts.map(a => (
                <tr key={a.id} className="hover:bg-zinc-800/20 transition-colors">
                  <td className="px-4 py-3"><a href={a.source_url} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-400 hover:underline font-mono truncate max-w-[200px] block">{a.source_url.replace(/^https?:\/\//, '').substring(0, 40)}</a></td>
                  <td className="px-4 py-3 text-xs text-zinc-400 capitalize">{a.platform}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-bold uppercase px-2 py-0.5 rounded border ${SEVERITY_STYLES[a.severity]?.badge ?? SEVERITY_STYLES.LOW.badge}`}>{a.severity}</span></td>
                  <td className="px-4 py-3"><span className={riskColor(a.confidence_score)}>{(a.confidence_score * 100).toFixed(0)}%</span></td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{a.detection_method ?? 'manual'}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-bold uppercase px-2 py-0.5 rounded border ${LOGO_STATUS[a.status] ?? LOGO_STATUS.NEW}`}>{a.status}</span></td>
                  <td className="px-4 py-3">
                    <select onChange={e => updateStatus(a.id, e.target.value)} defaultValue={a.status}
                      className="bg-zinc-800/60 border border-zinc-700/40 text-zinc-400 text-xs rounded-lg px-2 py-1">
                      <option value="NEW">New</option>
                      <option value="REVIEWED">Reviewed</option>
                      <option value="TAKEDOWN_REQ">Takedown</option>
                      <option value="RESOLVED">Resolved</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Phase 4: Takedown Center Tab
// ─────────────────────────────────────────────────────────────────────────────

interface TakedownItem {
  id: number; brand_id: number; target_type: string; target_url: string;
  target_description?: string; status: string; priority: string;
  notes?: string; registrar_contacted?: string; platform_ticket_id?: string;
  submitted_at?: string; resolved_at?: string; created_at?: string;
}

const TAKEDOWN_STATUSES = ['IDENTIFIED','SUBMITTED','IN_REVIEW','COMPLETED','REJECTED'] as const;
const TAKEDOWN_STATUS_STYLES: Record<string, string> = {
  IDENTIFIED: 'bg-zinc-700/30 text-zinc-400 border-zinc-700/30',
  SUBMITTED: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  IN_REVIEW: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  COMPLETED: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  REJECTED: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
};

function TakedownTab({ entities }: { entities: BrandEntity[] }) {
  const [selectedId, setSelectedId] = useState<number | null>(entities[0]?.id ?? null);
  const [items, setItems] = useState<TakedownItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [guidance, setGuidance] = useState<Record<number, any>>({});
  const [advanceId, setAdvanceId] = useState<number | null>(null);
  const [advanceStatus, setAdvanceStatus] = useState('');
  const [advanceNotes, setAdvanceNotes] = useState('');
  const [form, setForm] = useState({ target_type: 'domain', target_url: '', target_description: '', priority: 'MEDIUM', notes: '' });

  const load = useCallback(async (id: number) => {
    setLoading(true);
    try { setItems(await apiFetch<TakedownItem[]>(`/api/brand/takedowns?brand_id=${id}&limit=100`)); }
    catch { setItems([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (selectedId) load(selectedId); }, [selectedId, load]);

  const create = async () => {
    if (!selectedId || !form.target_url.trim()) { setMsg('Target URL is required.'); return; }
    setSubmitting(true); setMsg(null);
    try {
      await apiFetch(`/api/brand/takedowns/${selectedId}`, { method: 'POST', body: JSON.stringify(form) });
      setMsg('Takedown request created.'); setShowForm(false);
      load(selectedId);
    } catch (e: any) { setMsg(`Error: ${e.message}`); } finally { setSubmitting(false); }
  };

  const advance = async () => {
    if (!advanceId || !advanceStatus) return;
    try {
      await apiFetch(`/api/brand/takedowns/${advanceId}/status`, {
        method: 'PUT', body: JSON.stringify({ status: advanceStatus, notes: advanceNotes }),
      });
      setAdvanceId(null); setAdvanceStatus(''); setAdvanceNotes('');
      if (selectedId) load(selectedId);
    } catch (e: any) { setMsg(`Advance error: ${e.message}`); }
  };

  const getGuidance = async (id: number) => {
    if (guidance[id]) return;
    try {
      const g = await apiFetch<any>(`/api/brand/takedowns/${id}/guidance`);
      setGuidance(prev => ({ ...prev, [id]: g }));
    } catch {}
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-5 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <select value={selectedId ?? ''} onChange={e => setSelectedId(Number(e.target.value))}
            className="w-full bg-zinc-900/60 border border-zinc-700/60 text-zinc-200 text-sm rounded-xl px-4 py-2.5 appearance-none focus:outline-none focus:border-cyan-500/50">
            {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-zinc-400 pointer-events-none" />
        </div>
        <button onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2.5 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 text-sm font-medium rounded-xl border border-cyan-500/20 transition-all">
          <Plus className="w-4 h-4" /> Create Takedown
        </button>
        <button onClick={() => selectedId && load(selectedId)}
          className="flex items-center gap-2 px-3 py-2.5 bg-zinc-800/60 hover:bg-zinc-700/60 text-zinc-300 text-sm rounded-xl border border-zinc-700/40">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      {msg && <p className="mb-4 text-sm text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 rounded-xl px-4 py-2">{msg}</p>}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="mb-5 bg-zinc-900/60 border border-zinc-800/60 rounded-xl p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Target Type</label>
              <select value={form.target_type} onChange={e => setForm(f => ({ ...f, target_type: e.target.value }))}
                className="w-full bg-zinc-800/60 border border-zinc-700/40 text-zinc-200 text-sm rounded-xl px-3 py-2 focus:outline-none">
                <option value="domain">Domain</option>
                <option value="social_account">Social Account</option>
                <option value="mobile_app">Mobile App</option>
                <option value="dark_web_listing">Dark Web Listing</option>
                <option value="paste_site">Paste Site</option>
                <option value="logo_misuse">Logo Misuse</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Priority</label>
              <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                className="w-full bg-zinc-800/60 border border-zinc-700/40 text-zinc-200 text-sm rounded-xl px-3 py-2 focus:outline-none">
                <option value="LOW">Low</option><option value="MEDIUM">Medium</option><option value="HIGH">High</option><option value="CRITICAL">Critical</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-zinc-400 mb-1">Target URL *</label>
              <input value={form.target_url} onChange={e => setForm(f => ({ ...f, target_url: e.target.value }))} placeholder="https://fake-brand.com"
                className="w-full bg-zinc-800/60 border border-zinc-700/40 text-zinc-200 text-sm rounded-xl px-3 py-2 focus:outline-none" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-zinc-400 mb-1">Notes</label>
              <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
                className="w-full bg-zinc-800/60 border border-zinc-700/40 text-zinc-200 text-sm rounded-xl px-3 py-2 focus:outline-none resize-none" />
            </div>
            <div className="sm:col-span-2 flex justify-end gap-3">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-zinc-400 rounded-xl border border-zinc-700/40">Cancel</button>
              <button onClick={create} disabled={submitting}
                className="px-4 py-2 text-sm font-medium bg-cyan-500/20 text-cyan-400 rounded-xl border border-cyan-500/30 hover:bg-cyan-500/30 disabled:opacity-50">
                {submitting ? 'Creating…' : 'Create Request'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Advance status modal */}
      <AnimatePresence>
        {advanceId && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 w-full max-w-md shadow-2xl">
              <h4 className="text-sm font-bold text-zinc-200 mb-4">Advance Takedown Status</h4>
              <div className="space-y-3 mb-4">
                <select value={advanceStatus} onChange={e => setAdvanceStatus(e.target.value)}
                  className="w-full bg-zinc-800/60 border border-zinc-700/50 text-zinc-200 text-sm rounded-xl px-3 py-2.5 focus:outline-none">
                  <option value="">— Select next status —</option>
                  {TAKEDOWN_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <textarea value={advanceNotes} onChange={e => setAdvanceNotes(e.target.value)} rows={2} placeholder="Add notes…"
                  className="w-full bg-zinc-800/60 border border-zinc-700/50 text-zinc-200 text-sm rounded-xl px-3 py-2 focus:outline-none resize-none" />
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => { setAdvanceId(null); setAdvanceStatus(''); setAdvanceNotes(''); }}
                  className="px-4 py-2 text-sm text-zinc-400 rounded-xl border border-zinc-700/40">Cancel</button>
                <button onClick={advance} disabled={!advanceStatus}
                  className="px-4 py-2 text-sm font-medium bg-cyan-500/20 text-cyan-400 rounded-xl border border-cyan-500/30 disabled:opacity-50">
                  Advance
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 bg-zinc-800/40 animate-pulse rounded-xl" />)}</div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center"><Flag className="w-10 h-10 text-zinc-700 mx-auto mb-3" /><p className="text-zinc-400 text-sm">No takedown requests yet.</p></div>
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <div key={item.id} className="bg-zinc-900/60 border border-zinc-800/50 rounded-xl px-4 py-3 hover:border-zinc-700/60 transition-colors">
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded border ${TAKEDOWN_STATUS_STYLES[item.status]}`}>{item.status}</span>
                    <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded border ${SEVERITY_STYLES[item.priority]?.badge ?? SEVERITY_STYLES.LOW.badge}`}>{item.priority}</span>
                    <span className="text-xs text-zinc-400 bg-zinc-800/60 px-2 py-0.5 rounded border border-zinc-700/30">{item.target_type.replace('_',' ')}</span>
                  </div>
                  <p className="text-xs font-mono text-cyan-400 truncate">{item.target_url}</p>
                  {item.notes && <p className="text-xs text-zinc-400 mt-0.5 truncate">{item.notes.split('\n')[0]}</p>}
                  <p className="text-xs text-zinc-400 mt-0.5">{fmt(item.created_at)}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => { setAdvanceId(item.id); setAdvanceStatus(''); }}
                    className="px-3 py-1.5 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 text-xs font-medium rounded-lg border border-cyan-500/20">Advance</button>
                  <button onClick={() => getGuidance(item.id)}
                    className="px-3 py-1.5 bg-zinc-800/60 hover:bg-zinc-700/60 text-zinc-400 text-xs rounded-lg border border-zinc-700/40">Guide</button>
                </div>
              </div>
              {guidance[item.id] && (
                <div className="mt-3 bg-zinc-800/40 rounded-lg p-3 text-xs text-zinc-300">
                  <p className="font-medium text-zinc-200 mb-1">{guidance[item.id].method}</p>
                  {guidance[item.id].steps && (
                    <ol className="list-decimal list-inside space-y-1 text-zinc-400">
                      {guidance[item.id].steps.map((s: string, i: number) => <li key={i}>{s}</li>)}
                    </ol>
                  )}
                  {guidance[item.id].template && (
                    <pre className="mt-2 bg-zinc-900/60 rounded p-2 text-xs whitespace-pre-wrap text-zinc-400">{guidance[item.id].template.substring(0, 300)}…</pre>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Phase 4: Competitive Intelligence Tab
// ─────────────────────────────────────────────────────────────────────────────

interface CompetitorItem { id: number; name: string; domain: string; keywords: string[]; }
interface CompareResult {
  brand_name: string;
  primary_brand: { name: string; total_mentions: number; avg_sentiment: number; avg_risk: number; total_reach: number; platform_breakdown: Record<string, number> };
  competitors: Array<{ id: number; name: string; total_mentions: number; avg_sentiment: number; avg_risk: number; total_reach: number }>;
  mention_rank: number;
  total_tracked: number;
}

function CompetitiveTab({ entities }: { entities: BrandEntity[] }) {
  const [selectedId, setSelectedId] = useState<number | null>(entities[0]?.id ?? null);
  const [competitors, setCompetitors] = useState<CompetitorItem[]>([]);
  const [compare, setCompare] = useState<CompareResult | null>(null);
  const [loadingComp, setLoadingComp] = useState(false);
  const [loadingCmp, setLoadingCmp] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [adding, setAdding] = useState(false);
  const [days, setDays] = useState(7);
  const [form, setForm] = useState({ name: '', domain: '', keywords: '' });
  const [msg, setMsg] = useState<string | null>(null);

  const loadCompetitors = useCallback(async (id: number) => {
    setLoadingComp(true);
    try { setCompetitors(await apiFetch<CompetitorItem[]>(`/api/brand/competitors/${id}`)); }
    catch { setCompetitors([]); } finally { setLoadingComp(false); }
  }, []);

  const loadCompare = useCallback(async (id: number) => {
    setLoadingCmp(true);
    try { setCompare(await apiFetch<CompareResult>(`/api/brand/competitive/${id}?days=${days}`)); }
    catch { setCompare(null); } finally { setLoadingCmp(false); }
  }, [days]);

  useEffect(() => { if (selectedId) { loadCompetitors(selectedId); loadCompare(selectedId); } }, [selectedId, loadCompetitors, loadCompare]);

  const addComp = async () => {
    if (!selectedId || !form.name.trim()) { setMsg('Name is required.'); return; }
    setAdding(true); setMsg(null);
    try {
      await apiFetch(`/api/brand/competitors/${selectedId}`, {
        method: 'POST', body: JSON.stringify({ name: form.name, domain: form.domain, keywords: form.keywords ? form.keywords.split(',').map(k => k.trim()).filter(Boolean) : [] }),
      });
      setMsg(`Competitor "${form.name}" added.`); setShowForm(false); setForm({ name: '', domain: '', keywords: '' });
      loadCompetitors(selectedId);
    } catch (e: any) { setMsg(`Error: ${e.message}`); } finally { setAdding(false); }
  };

  const removeComp = async (compId: number, name: string) => {
    await apiFetch(`/api/brand/competitors/${compId}`, { method: 'DELETE' });
    setMsg(`"${name}" removed.`);
    if (selectedId) loadCompetitors(selectedId);
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-5 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <select value={selectedId ?? ''} onChange={e => setSelectedId(Number(e.target.value))}
            className="w-full bg-zinc-900/60 border border-zinc-700/60 text-zinc-200 text-sm rounded-xl px-4 py-2.5 appearance-none focus:outline-none focus:border-cyan-500/50">
            {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-zinc-400 pointer-events-none" />
        </div>
        <select value={days} onChange={e => setDays(Number(e.target.value))}
          className="bg-zinc-900/60 border border-zinc-700/60 text-zinc-300 text-sm rounded-xl px-3 py-2.5 focus:outline-none">
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
        </select>
        <button onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2.5 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 text-sm font-medium rounded-xl border border-cyan-500/20 transition-all">
          <Plus className="w-4 h-4" /> Add Competitor
        </button>
        <button onClick={() => selectedId && loadCompare(selectedId)}
          className="flex items-center gap-2 px-3 py-2.5 bg-zinc-800/60 hover:bg-zinc-700/60 text-zinc-300 text-sm rounded-xl border border-zinc-700/40">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      {msg && <p className="mb-4 text-sm text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 rounded-xl px-4 py-2">{msg}</p>}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="mb-5 bg-zinc-900/60 border border-zinc-800/60 rounded-xl p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="CompetitorCorp"
                className="w-full bg-zinc-800/60 border border-zinc-700/40 text-zinc-200 text-sm rounded-xl px-3 py-2 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Domain</label>
              <input value={form.domain} onChange={e => setForm(f => ({ ...f, domain: e.target.value }))} placeholder="competitor.com"
                className="w-full bg-zinc-800/60 border border-zinc-700/40 text-zinc-200 text-sm rounded-xl px-3 py-2 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Keywords <span className="text-zinc-400">(comma-sep)</span></label>
              <input value={form.keywords} onChange={e => setForm(f => ({ ...f, keywords: e.target.value }))} placeholder="competitor, their-product"
                className="w-full bg-zinc-800/60 border border-zinc-700/40 text-zinc-200 text-sm rounded-xl px-3 py-2 focus:outline-none" />
            </div>
            <div className="sm:col-span-3 flex justify-end gap-3">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-zinc-400 rounded-xl border border-zinc-700/40">Cancel</button>
              <button onClick={addComp} disabled={adding}
                className="px-4 py-2 text-sm font-medium bg-cyan-500/20 text-cyan-400 rounded-xl border border-cyan-500/30 hover:bg-cyan-500/30 disabled:opacity-50">
                {adding ? 'Adding…' : 'Add'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Competitor list */}
        <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-2xl p-5">
          <h4 className="text-sm font-bold text-zinc-300 mb-3">Tracked Competitors</h4>
          {loadingComp ? <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-10 bg-zinc-800/40 animate-pulse rounded-lg" />)}</div>
          : competitors.length === 0 ? <p className="text-xs text-zinc-400 text-center py-8">No competitors tracked.</p>
          : (
            <div className="space-y-2">
              {competitors.map(c => (
                <div key={c.id} className="flex items-center justify-between px-3 py-2 bg-zinc-800/40 rounded-lg border border-zinc-700/30">
                  <div>
                    <p className="text-xs font-medium text-zinc-200">{c.name}</p>
                    {c.domain && <p className="text-xs text-zinc-400 font-mono">{c.domain}</p>}
                  </div>
                  <button onClick={() => removeComp(c.id, c.name)}
                    className="p-1.5 hover:bg-rose-500/10 text-zinc-400 hover:text-rose-400 rounded-lg transition-all">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Comparison table */}
        <div className="lg:col-span-2 bg-zinc-900/60 border border-zinc-800/60 rounded-2xl p-5">
          <h4 className="text-sm font-bold text-zinc-300 mb-3">
            Comparison — Last {days}d
            {compare && <span className="ml-2 text-xs text-zinc-400 font-normal">Mention rank #{compare.mention_rank} of {compare.total_tracked}</span>}
          </h4>
          {loadingCmp ? (
            <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-10 bg-zinc-800/40 animate-pulse rounded-lg" />)}</div>
          ) : !compare ? (
            <p className="text-xs text-zinc-400 text-center py-8">Add competitors and refresh to see comparison.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-zinc-400 uppercase border-b border-zinc-800/60">
                  <th className="pb-3 text-left">Brand</th>
                  <th className="pb-3 text-right">Mentions</th>
                  <th className="pb-3 text-right">Neg%</th>
                  <th className="pb-3 text-right">Avg Risk</th>
                  <th className="pb-3 text-right">Reach</th>
                </tr></thead>
                <tbody className="divide-y divide-zinc-800/40">
                  {[
                    { ...compare.primary_brand, _primary: true },
                    ...compare.competitors.map(c => ({ ...c, _primary: false })),
                  ].map((row: any) => (
                    <tr key={row.name} className={`${row._primary ? 'bg-cyan-500/5' : ''} hover:bg-zinc-800/20 transition-colors`}>
                      <td className="py-2.5 text-sm">
                        <span className={`font-medium ${row._primary ? 'text-cyan-400' : 'text-zinc-300'}`}>{row.name}</span>
                        {row._primary && <span className="ml-1.5 text-xs text-cyan-500 bg-cyan-500/10 border border-cyan-500/20 px-1.5 py-0.5 rounded">You</span>}
                      </td>
                      <td className="py-2.5 text-right text-zinc-200 font-semibold">{row.total_mentions?.toLocaleString()}</td>
                      <td className="py-2.5 text-right"><span className={riskColor(row.negative_mentions / Math.max(row.total_mentions, 1))}>{row.total_mentions > 0 ? ((row.negative_mentions / row.total_mentions) * 100).toFixed(0) : 0}%</span></td>
                      <td className="py-2.5 text-right"><span className={riskColor(row.avg_risk)}>{((row.avg_risk ?? 0) * 100).toFixed(0)}</span></td>
                      <td className="py-2.5 text-right text-zinc-400 text-xs">{row.total_reach?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Phase 4 Final: Multi-Brand Tenant Console Tab
// ─────────────────────────────────────────────────────────────────────────────

interface Tenant {
  id: number;
  name: string;
  slug: string;
  plan: string;
  max_brands: number;
  brand_count: number;
  is_active: boolean;
  created_at?: string;
  theme?: {
    portal_title?: string;
    custom_logo_url?: string;
    primary_colour?: string;
    accent_colour?: string;
    custom_domain?: string;
  };
}

function TenantConsoleTab({ entities }: { entities: BrandEntity[] }) {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [tenantBrands, setTenantBrands] = useState<BrandEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showTheme, setShowTheme] = useState(false);
  const [newTenant, setNewTenant] = useState({ name: '', slug: '', plan: 'starter', max_brands: 5 });
  const [theme, setTheme] = useState({ portal_title: '', custom_logo_url: '', primary_colour: '#1a1a2e', accent_colour: '#e94560', custom_domain: '' });
  const [saving, setSaving] = useState(false);

  const apiFetch = async <T,>(path: string, opts?: RequestInit): Promise<T> => {
    const r = await fetch(`${API_BASE_URL}${path}`, opts);
    if (!r.ok) throw new Error(await r.text());
    return r.json() as Promise<T>;
  };

  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch<Tenant[]>('/api/tenants');
      setTenants(data);
    } catch { setTenants([]); }
    finally { setLoading(false); }
  }, []);

  const selectTenant = async (t: Tenant) => {
    setSelectedTenant(t);
    setShowTheme(false);
    try {
      const [brands, themeData] = await Promise.all([
        apiFetch<BrandEntity[]>(`/api/tenants/${t.id}/brands`),
        apiFetch<{ primary_colour: string; accent_colour: string; portal_title: string; custom_logo_url: string; custom_domain: string }>(`/api/tenants/${t.id}/theme`),
      ]);
      setTenantBrands(brands);
      setTheme({ portal_title: themeData.portal_title || '', custom_logo_url: themeData.custom_logo_url || '', primary_colour: themeData.primary_colour || '#1a1a2e', accent_colour: themeData.accent_colour || '#e94560', custom_domain: themeData.custom_domain || '' });
    } catch { setTenantBrands([]); }
  };

  const createTenant = async () => {
    if (!newTenant.name || !newTenant.slug) return;
    setSaving(true);
    try {
      await apiFetch('/api/tenants', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newTenant) });
      setShowForm(false);
      setNewTenant({ name: '', slug: '', plan: 'starter', max_brands: 5 });
      loadTenants();
    } catch (e) { alert('Error: ' + e); }
    finally { setSaving(false); }
  };

  const saveTheme = async () => {
    if (!selectedTenant) return;
    setSaving(true);
    try {
      await apiFetch(`/api/tenants/${selectedTenant.id}/theme`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(theme) });
      alert('Theme saved!');
    } catch (e) { alert('Error: ' + e); }
    finally { setSaving(false); }
  };

  const removeBrand = async (brandId: number) => {
    if (!selectedTenant) return;
    await apiFetch(`/api/tenants/${selectedTenant.id}/brands/${brandId}`, { method: 'DELETE' });
    selectTenant(selectedTenant);
  };

  const assignBrand = async (brandId: number) => {
    if (!selectedTenant) return;
    try {
      await apiFetch(`/api/tenants/${selectedTenant.id}/brands/${brandId}`, { method: 'POST' });
      selectTenant(selectedTenant);
    } catch (e) { alert('Error: ' + e); }
  };

  useEffect(() => { loadTenants(); }, [loadTenants]);

  const planColour = (plan: string) => plan === 'enterprise' ? 'text-amber-400' : plan === 'professional' ? 'text-purple-400' : plan === 'starter' ? 'text-cyan-400' : 'text-zinc-400';

  const unassigned = entities.filter(e => !tenantBrands.find(b => b.id === e.id));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Multi-Brand Tenant Console</h2>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-2 px-3 py-1.5 bg-cyan-500/15 border border-cyan-500/30 text-cyan-400 rounded-lg text-sm hover:bg-cyan-500/25 transition-all">
          <Plus className="w-4 h-4" /> New Tenant
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="bg-zinc-900/60 border border-zinc-700/50 rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-medium text-zinc-300">Create New Tenant</h3>
          <div className="grid grid-cols-2 gap-3">
            <input value={newTenant.name} onChange={e => setNewTenant(p => ({ ...p, name: e.target.value }))} placeholder="Organisation name" className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-cyan-500" />
            <input value={newTenant.slug} onChange={e => setNewTenant(p => ({ ...p, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))} placeholder="slug (e.g. acme-corp)" className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-cyan-500" />
            <select value={newTenant.plan} onChange={e => setNewTenant(p => ({ ...p, plan: e.target.value }))} className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500">
              {['free', 'starter', 'professional', 'enterprise'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
            </select>
            <input type="number" value={newTenant.max_brands} onChange={e => setNewTenant(p => ({ ...p, max_brands: +e.target.value }))} placeholder="Max brands" className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500" />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowForm(false)} className="px-4 py-1.5 text-sm text-zinc-400 hover:text-white">Cancel</button>
            <button onClick={createTenant} disabled={saving} className="px-4 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-sm disabled:opacity-50">{saving ? 'Creating…' : 'Create Tenant'}</button>
          </div>
        </div>
      )}

      {loading && <p className="text-zinc-400 text-sm">Loading tenants…</p>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tenant list */}
        <div className="space-y-3">
          {tenants.length === 0 && !loading && (
            <div className="text-center py-10 text-zinc-400 text-sm">No tenants yet. Create one above.</div>
          )}
          {tenants.map(t => (
            <button key={t.id} onClick={() => selectTenant(t)} className={`w-full text-left p-4 rounded-xl border transition-all ${selectedTenant?.id === t.id ? 'border-cyan-500/40 bg-cyan-500/10' : 'border-zinc-700/50 bg-zinc-900/50 hover:border-zinc-600'}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-white text-sm">{t.name}</p>
                  <p className="text-zinc-400 text-xs mt-0.5">{t.slug}</p>
                </div>
                <span className={`text-xs font-medium ${planColour(t.plan)}`}>{t.plan}</span>
              </div>
              <div className="flex items-center gap-3 mt-2 text-xs text-zinc-400">
                <span>{t.brand_count}/{t.max_brands} brands</span>
                <span className={`w-1.5 h-1.5 rounded-full ${t.is_active ? 'bg-green-400' : 'bg-zinc-600'}`} />
              </div>
            </button>
          ))}
        </div>

        {/* Tenant detail */}
        {selectedTenant ? (
          <div className="lg:col-span-2 space-y-5">
            {/* Assigned brands */}
            <div className="bg-zinc-900/60 border border-zinc-700/50 rounded-xl p-5">
              <h3 className="text-sm font-medium text-zinc-300 mb-3">Assigned Brands</h3>
              {tenantBrands.length === 0 ? (
                <p className="text-zinc-400 text-xs">No brands assigned. Add from the list below.</p>
              ) : (
                <div className="space-y-2">
                  {tenantBrands.map(b => (
                    <div key={b.id} className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg">
                      <span className="text-sm text-zinc-200">{b.name}</span>
                      <button onClick={() => removeBrand(b.id)} className="text-rose-400 hover:text-rose-300 text-xs">Remove</button>
                    </div>
                  ))}
                </div>
              )}
              {unassigned.length > 0 && (
                <div className="mt-3 pt-3 border-t border-zinc-700/50">
                  <p className="text-xs text-zinc-400 mb-2">Add brand:</p>
                  <div className="flex flex-wrap gap-2">
                    {unassigned.map(b => (
                      <button key={b.id} onClick={() => assignBrand(b.id)} className="px-2 py-1 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded text-xs">+ {b.name}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* White-label theme */}
            <div className="bg-zinc-900/60 border border-zinc-700/50 rounded-xl p-5">
              <button onClick={() => setShowTheme(!showTheme)} className="flex items-center gap-2 text-sm font-medium text-zinc-300 w-full">
                {showTheme ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                White-Label Theme Configuration
              </button>
              {showTheme && (
                <div className="mt-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-zinc-400 mb-1">Portal Title</label>
                      <input value={theme.portal_title} onChange={e => setTheme(p => ({ ...p, portal_title: e.target.value }))} placeholder="CrisisShield" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-cyan-500" />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-400 mb-1">Custom Domain</label>
                      <input value={theme.custom_domain} onChange={e => setTheme(p => ({ ...p, custom_domain: e.target.value }))} placeholder="monitor.client.com" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-cyan-500" />
                    </div>
                    <div>
                      <label className="block text-xs text-zinc-400 mb-1">Logo URL</label>
                      <input value={theme.custom_logo_url} onChange={e => setTheme(p => ({ ...p, custom_logo_url: e.target.value }))} placeholder="https://…/logo.png" className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-cyan-500" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-zinc-400 mb-1">Primary Colour</label>
                        <div className="flex gap-2 items-center">
                          <input type="color" value={theme.primary_colour} onChange={e => setTheme(p => ({ ...p, primary_colour: e.target.value }))} className="h-8 w-8 rounded cursor-pointer bg-transparent border-0" />
                          <input value={theme.primary_colour} onChange={e => setTheme(p => ({ ...p, primary_colour: e.target.value }))} className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-500" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-zinc-400 mb-1">Accent Colour</label>
                        <div className="flex gap-2 items-center">
                          <input type="color" value={theme.accent_colour} onChange={e => setTheme(p => ({ ...p, accent_colour: e.target.value }))} className="h-8 w-8 rounded cursor-pointer bg-transparent border-0" />
                          <input value={theme.accent_colour} onChange={e => setTheme(p => ({ ...p, accent_colour: e.target.value }))} className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-500" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button onClick={saveTheme} disabled={saving} className="px-4 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-sm disabled:opacity-50">{saving ? 'Saving…' : 'Save Theme'}</button>
                  </div>
                  {/* Live preview */}
                  <div className="mt-3 rounded-lg p-4 border" style={{ borderColor: theme.primary_colour, background: theme.primary_colour + '22' }}>
                    <p className="text-xs text-zinc-400 mb-1">Live preview:</p>
                    <div className="flex items-center gap-3">
                      {theme.custom_logo_url && <img src={theme.custom_logo_url} alt="logo" className="h-8 w-8 object-contain rounded" onError={() => {}} />}
                      <span className="font-bold text-white">{theme.portal_title || 'CrisisShield'}</span>
                      <span className="ml-auto px-2 py-0.5 rounded text-xs font-medium text-white" style={{ background: theme.accent_colour }}>LIVE</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="lg:col-span-2 flex items-center justify-center text-zinc-400 text-sm">
            Select a tenant from the list to view details
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Main Page
// ─────────────────────────────────────────────────────────────────────────────

type Tab = 'overview' | 'config' | 'alerts' | 'domains' | 'impersonation' | 'darkweb' | 'reports' | 'simulator' | 'logo' | 'takedown' | 'competitive' | 'tenants';

export default function BrandProtection() {
  const [tab, setTab] = useState<Tab>('overview');
  const [entities, setEntities] = useState<BrandEntity[]>([]);
  const [loadingEntities, setLoadingEntities] = useState(true);

  const loadEntities = useCallback(async () => {
    setLoadingEntities(true);
    try {
      const data = await apiFetch<BrandEntity[]>('/api/brand/entities?active_only=true');
      setEntities(data);
    } catch {
      setEntities([]);
    } finally {
      setLoadingEntities(false);
    }
  }, []);

  useEffect(() => { loadEntities(); }, [loadEntities]);

  const TABS: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: 'overview', label: 'Overview', icon: BarChart2 },
    { id: 'config', label: 'Configuration', icon: Settings },
    { id: 'alerts', label: 'Alerts', icon: Bell },
    { id: 'domains', label: 'Domains', icon: Shield },
    { id: 'impersonation', label: 'Impersonation', icon: ShieldAlert },
    { id: 'darkweb', label: 'Dark Web', icon: Activity },
    { id: 'reports', label: 'Reports', icon: TrendingDown },
    { id: 'simulator', label: 'Simulator', icon: Cpu },
    { id: 'logo', label: 'Logo Misuse', icon: Image },
    { id: 'takedown', label: 'Takedowns', icon: Flag },
    { id: 'competitive', label: 'Competitive', icon: GitBranch },
    { id: 'tenants', label: 'Multi-Brand', icon: Users },
  ];

  return (
    <div>
      {/* Page header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-500/20 rounded-xl">
              <Shield className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-xl md:text-3xl font-bold tracking-tight text-white">Brand Protection</h1>
              <p className="text-zinc-400 text-sm">Monitor brand mentions, credential leaks, and reputation risks</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-400 bg-zinc-900/50 px-4 py-2 rounded-full border border-zinc-800/50">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500" />
          </span>
          {loadingEntities ? 'Loading…' : `${entities.length} brand${entities.length !== 1 ? 's' : ''} monitored`}
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 bg-zinc-900/60 border border-zinc-800/50 rounded-xl p-1 mb-6 overflow-x-auto">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === id
                ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/20'
                : 'text-zinc-400 hover:text-zinc-300'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
            {id === 'alerts' && entities.length > 0 && (
              <span className="ml-1 w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18 }}
        >
          {tab === 'overview' && <OverviewTab entities={entities} />}
          {tab === 'config' && <ConfigTab entities={entities} onRefresh={loadEntities} />}
          {tab === 'alerts' && <AlertsTab entities={entities} />}
          {tab === 'domains' && <DomainsTab entities={entities} />}
          {tab === 'impersonation' && <ImpersonationTab entities={entities} />}
          {tab === 'darkweb' && <DarkWebTab entities={entities} />}
          {tab === 'reports' && <ReportsTab entities={entities} />}
          {tab === 'simulator' && <SimulatorTab entities={entities} />}
          {tab === 'logo' && <LogoMisuseTab entities={entities} />}
          {tab === 'takedown' && <TakedownTab entities={entities} />}
          {tab === 'competitive' && <CompetitiveTab entities={entities} />}
          {tab === 'tenants' && <TenantConsoleTab entities={entities} />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
